// © 2025 Ananda Kumar. All rights reserved.
// Unauthorized copying of this file, via any medium, is strictly prohibited.

import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, fromEvent, merge, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ZoomDetectorService implements OnDestroy {
  private zoomLevel$ = new BehaviorSubject<number>(this.getZoomLevel());
  private subscriptions = new Subscription();

  constructor(private ngZone: NgZone) {
    this.ngZone.runOutsideAngular(() => {
      const viewportResize$ = window.visualViewport
        ? fromEvent(window.visualViewport, 'resize')
        : fromEvent(window, 'resize');

      const fallbackResize$ = fromEvent(window, 'resize');

      this.subscriptions.add(
        merge(viewportResize$, fallbackResize$).subscribe(() => {
          const currentZoom = this.getZoomLevel();
          if (this.zoomLevel$.value !== currentZoom) {
            this.ngZone.run(() => {
              this.zoomLevel$.next(currentZoom);
            });
          }
        })
      );
    });
  }

  get zoom$() {
    return this.zoomLevel$.asObservable();
  }

  get currentZoom(): number {
    return this.zoomLevel$.value;
  }

  get isZoomedIn(): boolean {
    return this.currentZoom > 1;
  }

  private getZoomLevel(): number {
    if (window.visualViewport) {
      return window.visualViewport.scale;
    }

    const ratio = window.outerWidth / window.innerWidth;
    return ratio || 1;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
